import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Loader2, 
  Database,
  Download,
  Shield,
  Info,
  CheckCircle2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

export function BackupTab({ 
  api,
  backupLoading,
  setBackupLoading,
  lastBackup,
  setLastBackup
}) {
  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await api.get('/admin/backup');
      const backupData = response.data;
      
      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `lux-backup-${format(new Date(), 'yyyy-MM-dd-HHmm')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setLastBackup({
        timestamp: backupData.backup_timestamp,
        totalDocuments: backupData.summary?.total_documents || 0
      });
      
      toast.success(`Backup downloaded! ${backupData.summary?.total_documents || 0} documents exported.`);
    } catch (error) {
      console.error('Backup error:', error);
      toast.error('Failed to create backup');
    } finally {
      setBackupLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-[#2A3240] border-white/10">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-md">
              <Database className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <CardTitle className="text-xl text-white">Database Backup</CardTitle>
              <CardDescription className="text-gray-400">Export all data for safekeeping before major changes</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-200">
                <p className="font-medium mb-1">Why backup?</p>
                <ul className="list-disc list-inside text-blue-300 space-y-1">
                  <li>Before adding new features (like soft delete or client portals)</li>
                  <li>Regularly for peace of mind</li>
                  <li>Save the JSON file locally - it contains all your data</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-[#1E2530] rounded-lg border border-white/5">
            <h4 className="text-white font-medium mb-2">What gets backed up?</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-400">
              <span>• Users</span>
              <span>• Projects</span>
              <span>• Tenders</span>
              <span>• Contacts</span>
              <span>• Invoices</span>
              <span>• Variations</span>
              <span>• Surveys</span>
              <span>• Schedules</span>
              <span>• Time Entries</span>
              <span>• Leave Requests</span>
              <span>• Settings</span>
              <span>• Email Templates</span>
            </div>
          </div>

          {lastBackup && (
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              <span className="text-green-300 text-sm">
                Last backup: {format(parseISO(lastBackup.timestamp), 'dd MMM yyyy HH:mm')} - {lastBackup.totalDocuments} documents
              </span>
            </div>
          )}

          <Button
            onClick={handleBackup}
            disabled={backupLoading}
            className="w-full bg-green-600 hover:bg-green-700 text-white"
            data-testid="download-backup-btn"
          >
            {backupLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating Backup...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download Database Backup
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Security Info */}
      <Card className="bg-[#2A3240] border-white/10">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/10 rounded-md">
              <Shield className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-xl text-white">Security Status</CardTitle>
              <CardDescription className="text-gray-400">Current security configuration</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-[#1E2530] rounded-lg">
              <span className="text-gray-300">JWT Authentication</span>
              <Badge className="bg-green-500/20 text-green-400">Active</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-[#1E2530] rounded-lg">
              <span className="text-gray-300">Password Hashing (bcrypt)</span>
              <Badge className="bg-green-500/20 text-green-400">Active</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-[#1E2530] rounded-lg">
              <span className="text-gray-300">Role-Based Access Control</span>
              <Badge className="bg-green-500/20 text-green-400">Active</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-[#1E2530] rounded-lg">
              <span className="text-gray-300">API Route Protection</span>
              <Badge className="bg-green-500/20 text-green-400">Active</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
